/**
 * ModelRenderer: renders the 3D model from one of three input paths:
 *   1. Pre-loaded Object3D (AMF/3MF imports)
 *   2. Colored geometries (multi-colour OpenSCAD output)
 *   3. Single geometry (monochrome OpenSCAD output)
 *
 * Material management for imported objects is handled by useMaterialManager
 * (called by the parent). This component owns only the declarative JSX
 * materials for the colored/single geometry paths.
 *
 * Automatically switches to MeshPhysicalMaterial when physical properties
 * (clearcoat, sheen, iridescence, transmission) are in use — either from
 * per-object material_def() overrides or from the scene-level toggle.
 *
 * When grayscaleMode is true, all object colors are converted to their
 * luminance-equivalent gray and materials are forced to roughness=1,
 * metalness=0, envMapIntensity=0 for flat non-reflective geometry debugging.
 */

import { forwardRef, useMemo, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import type { MaterialSettings } from '@/contexts/SceneSettingsContext';
import type { ColoredGeometry } from '@/components/viewer/types';
import type { InteractiveConfig } from '@shared/types';
import type { CloneDef } from '@/worker/types';
import {
  createMaterial,
  needsPhysicalMaterial,
  disposeMaterial,
} from '@/components/viewer/materials/materialUtils';
import { generateFaceConstrainedUVs } from '@/utils/uvUtils';
import { AnimatedMotionMesh } from './MotionAnimator';

/** Compute the luminance-equivalent gray for an RGB color (ITU-R BT.709). */
function toLumaGray(r: number, g: number, b: number): THREE.Color {
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return new THREE.Color(luma, luma, luma);
}

interface ModelRendererProps {
  object?: THREE.Object3D;
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  materialSettings: MaterialSettings;
  wireframeOpacity: number;
  /** When true, strip all color and use flat non-reflective materials. */
  grayscaleMode?: boolean;
  /** Ref to the live JS-driven texture map: uuid → channel → texture */
  textureMapRef?: React.MutableRefObject<
    Map<string, Map<string, THREE.Texture>>
  >;
  /** Incremented when a new texture is registered; triggers material update */
  textureVersion?: number;
  /** Ref to the live shader material map: uuid → ShaderMaterial */
  shaderMaterialMapRef?: React.MutableRefObject<
    Map<string, THREE.ShaderMaterial>
  >;
  /** Incremented when a new shader material is ready; triggers material swap */
  shaderVersion?: number;
  /**
   * Called when the user clicks an interactive mesh (one wrapped with
   * interactive() in the SCAD source). The caller is responsible for
   * toggling or setting the named variable and triggering a recompile.
   */
  onInteraction?: (config: InteractiveConfig) => void;
  /**
   * When true, all motion() animations are frozen.
   * Shared with the light LFO/orbit pause toggle.
   */
  animationsPaused?: boolean;
  /**
   * Clone placements declared via clone() in OpenSCAD source.
   * Each entry references a source geometry by key and specifies a transform.
   */
  clones?: CloneDef[];
}

export const ModelRenderer = forwardRef<THREE.Group, ModelRendererProps>(
  function ModelRenderer(
    {
      object,
      geometry,
      coloredGeometries,
      color,
      materialSettings,
      wireframeOpacity,
      grayscaleMode = false,
      textureMapRef,
      textureVersion,
      shaderMaterialMapRef,
      shaderVersion,
      onInteraction,
      animationsPaused = false,
      clones,
    },
    ref,
  ) {
    // When wireframe is fully opaque, don't render solid meshes at all
    if (wireframeOpacity >= 1) {
      return <group ref={ref} />;
    }

    return (
      <group ref={ref}>
        {object ? (
          // Path 1: pre-loaded 3D object (AMF/3MF)
          <primitive
            object={object}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0, 0]}
          />
        ) : coloredGeometries.length > 0 ? (
          // Path 2: multi-colour geometries from OpenSCAD
          <ColoredGeometriesMesh
            coloredGeometries={coloredGeometries}
            clones={clones}
            materialSettings={materialSettings}
            wireframeOpacity={wireframeOpacity}
            grayscaleMode={grayscaleMode}
            textureMapRef={textureMapRef}
            textureVersion={textureVersion}
            shaderMaterialMapRef={shaderMaterialMapRef}
            shaderVersion={shaderVersion}
            onInteraction={onInteraction}
            animationsPaused={animationsPaused}
          />
        ) : geometry ? (
          // Path 3: single geometry fallback
          <SingleGeometryMesh
            geometry={geometry}
            color={color}
            materialSettings={materialSettings}
            wireframeOpacity={wireframeOpacity}
            grayscaleMode={grayscaleMode}
          />
        ) : null}
      </group>
    );
  },
);

// ---------------------------------------------------------------------------
// Individual colored geometry mesh with proper material lifecycle
// ---------------------------------------------------------------------------

function ColoredGeometryItem({
  cg,
  materialSettings,
  wireframeOpacity,
  grayscaleMode,
  textureMapRef,
  textureVersion,
  shaderMaterialMapRef,
  shaderVersion,
  onInteraction,
  animationsPaused = false,
}: {
  cg: ColoredGeometry;
  materialSettings: MaterialSettings;
  wireframeOpacity: number;
  grayscaleMode: boolean;
  textureMapRef?: React.MutableRefObject<
    Map<string, Map<string, THREE.Texture>>
  >;
  /** Bumped by parent when a new texture is registered */
  textureVersion?: number;
  /** Ref to the live shader material map: uuid → ShaderMaterial */
  shaderMaterialMapRef?: React.MutableRefObject<
    Map<string, THREE.ShaderMaterial>
  >;
  /** Bumped by parent when a new shader material is ready */
  shaderVersion?: number;
  onInteraction?: (config: InteractiveConfig) => void;
  animationsPaused?: boolean;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { gl } = useThree();

  const handleClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      if (!cg.interactive || !onInteraction) return;
      e.stopPropagation();
      onInteraction(cg.interactive);
    },
    [cg.interactive, onInteraction],
  );

  const handlePointerOver = useCallback(() => {
    if (!cg.interactive) return;
    gl.domElement.style.cursor = 'pointer';
  }, [cg.interactive, gl]);

  const handlePointerOut = useCallback(() => {
    if (!cg.interactive) return;
    gl.domElement.style.cursor = '';
  }, [cg.interactive, gl]);

  // Reset cursor on unmount to avoid a stuck pointer cursor
  useEffect(() => {
    return () => {
      if (cg.interactive) {
        gl.domElement.style.cursor = '';
      }
    };
  }, [cg.interactive, gl]);

  const baseTransparent = cg.color[3] < 1 || cg.isGhost;
  const baseOpacity = cg.isGhost ? 0.3 : cg.color[3];
  const finalOpacity = baseOpacity * (1 - wireframeOpacity);
  const isTransparent = baseTransparent || wireframeOpacity > 0;

  const usePhysical =
    !cg.isGhost &&
    !grayscaleMode &&
    !cg.shaderScript &&
    needsPhysicalMaterial(cg.material, materialSettings);

  /** Helper: read one channel from the nested texture map */
  const getChannelTexture = (channel: string): THREE.Texture | undefined =>
    textureMapRef?.current.get(cg.geometry.uuid)?.get(channel);

  /**
   * When cg.shaderScript is set the ShaderMaterial is owned by useShaderScripts
   * (via shaderMaterialMapRef). We read it on render — no creation here.
   * When null the worker hasn't finished yet; we fall back to a placeholder.
   */
  const shaderMaterial: THREE.ShaderMaterial | null =
    cg.shaderScript && shaderMaterialMapRef
      ? (shaderMaterialMapRef.current.get(cg.geometry.uuid) ?? null)
      : null;
  // shaderVersion is declared only to force re-render when the material is ready
  void shaderVersion;

  // Create material imperatively so we can choose the correct class.
  // In grayscale mode we always use MeshStandardMaterial (no physical props).
  // When a shader material is used, createMaterial() is still called to produce
  // a cheap placeholder that is swapped out once the worker sends READY.
  const material = useMemo(() => {
    const rawColor = cg.isGhost
      ? new THREE.Color(0.6, 0.6, 0.8)
      : new THREE.Color(cg.color[0], cg.color[1], cg.color[2]);

    const meshColor = grayscaleMode
      ? toLumaGray(rawColor.r, rawColor.g, rawColor.b)
      : rawColor;

    // In grayscale mode, override material settings to flat/non-reflective.
    const effectiveSettings: MaterialSettings = grayscaleMode
      ? {
          ...materialSettings,
          roughness: 1.0,
          metalness: 0.0,
          envMapIntensity: 0,
        }
      : materialSettings;

    return createMaterial(meshColor, effectiveSettings, {
      perObject: grayscaleMode ? undefined : cg.material,
      isGhost: cg.isGhost,
      opacity: finalOpacity,
      transparent: isTransparent,
      // Textures are stripped in grayscale mode (color information).
      // Also skip for shader materials — the shader handles its own appearance.
      map: cg.isGhost || grayscaleMode || cg.shaderScript ? undefined : getChannelTexture('color'),
      roughnessMap:
        cg.isGhost || grayscaleMode || cg.shaderScript
          ? undefined
          : getChannelTexture('roughness'),
      metalnessMap:
        cg.isGhost || grayscaleMode || cg.shaderScript
          ? undefined
          : getChannelTexture('metalness'),
      normalMap:
        cg.isGhost || grayscaleMode || cg.shaderScript ? undefined : getChannelTexture('normal'),
      alphaMap:
        cg.isGhost || grayscaleMode || cg.shaderScript ? undefined : getChannelTexture('alpha'),
      emissiveMap:
        cg.isGhost || grayscaleMode || cg.shaderScript ? undefined : getChannelTexture('emissive'),
      aoMap: cg.isGhost || grayscaleMode || cg.shaderScript ? undefined : getChannelTexture('ao'),
    });
    // We want to recreate when physical mode or grayscale changes, but update in-place otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cg, usePhysical, grayscaleMode]);

  // Wire the live textures into the material whenever a new texture is registered.
  // textureVersion is bumped by TextureScriptManager once per worker spawn,
  // so this effect runs at most once per compile — not every frame.
  // In grayscale mode, skip texture wiring (textures carry color information).
  useEffect(() => {
    if (!cg.isGhost && !grayscaleMode && textureMapRef) {
      const uuid = cg.geometry.uuid;
      const channelMap = textureMapRef.current.get(uuid);

      const matAsRecord = material as unknown as Record<string, unknown>;

      // Build a lookup from channel name → face constraint (if any) from the
      // textureScripts metadata that was extracted from the SCAD echo output.
      const faceByChannel = new Map<string, string>();
      if (cg.textureScripts) {
        for (const ts of cg.textureScripts) {
          if (ts.face) faceByChannel.set(ts.channel, ts.face);
        }
      }

      // Track which UV slot has been allocated for face-constrained textures on
      // this geometry.  Three.js MeshStandardMaterial supports two UV channels:
      //   channel 0 → "uv"  attribute (primary, used by all unconstrained textures)
      //   channel 1 → "uv1" attribute (secondary, used by face-constrained textures)
      //
      // The first face-constrained texture generates a "uv1" attribute and sets
      // texture.channel = 1. A second texture with a *different* face value on
      // the same geometry cannot be handled with a single additional UV slot —
      // in that case it falls back to unconstrained projection (channel 0) and
      // a console warning is emitted.
      let faceUVGenerated =
        cg.geometry.attributes['uv1'] != null
          ? (cg.geometry.attributes['uv1'] as THREE.BufferAttribute)
          : null;

      const setChannel = (
        slot: keyof THREE.MeshStandardMaterial,
        channel: string,
      ) => {
        const live = channelMap?.get(channel) ?? null;
        if (matAsRecord[slot] !== live) {
          console.log(
            `[ModelRenderer] Wiring ${channel} texture for uuid ${uuid}:`,
            live ? 'found' : 'null',
          );
          matAsRecord[slot] = live;
        }

        // Apply face-constrained UV if this channel has a face= restriction
        if (live && faceByChannel.has(channel)) {
          const face = faceByChannel.get(channel)!;

          if (!faceUVGenerated) {
            // First face-constrained texture on this geometry — generate uv1
            generateFaceConstrainedUVs(cg.geometry, face, 'uv1');
            faceUVGenerated = cg.geometry.attributes[
              'uv1'
            ] as THREE.BufferAttribute;
            console.log(
              `[ModelRenderer] Generated face-constrained UVs for uuid ${uuid}, face="${face}", slot="${channel}"`,
            );
          } else {
            // uv1 already exists — check if this face matches the existing one.
            // If different, we can't support it with the available UV slots.
            // We still wire the texture (on channel 1) so it shows something.
            console.log(
              `[ModelRenderer] Reusing existing uv1 for face-constrained texture uuid=${uuid}, channel=${channel}`,
            );
          }

          // Point the texture to the secondary UV channel
          live.channel = 1;

          // Face-constrained textures rely on alpha=0 for non-matching faces,
          // so the material must be transparent to respect those pixels.
          material.transparent = true;
        } else if (live) {
          // Unconstrained texture — use primary UV channel (0, the default)
          live.channel = 0;
        }
      };

      setChannel('map', 'color');
      setChannel('roughnessMap', 'roughness');
      setChannel('metalnessMap', 'metalness');
      setChannel('normalMap', 'normal');
      setChannel('alphaMap', 'alpha');
      setChannel('emissiveMap', 'emissive');
      setChannel('aoMap', 'ao');

      // Alpha map requires transparent mode
      if (channelMap?.has('alpha')) {
        material.transparent = true;
        material.depthWrite = false;
      }

      material.needsUpdate = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textureVersion, material, cg.geometry.uuid, cg.isGhost, grayscaleMode]);

  // Update material properties in-place when settings change (without recreating)
  useEffect(() => {
    if (grayscaleMode) {
      // Grayscale override: flat, non-reflective, luma-gray color
      const rawColor = cg.isGhost
        ? new THREE.Color(0.6, 0.6, 0.8)
        : new THREE.Color(cg.color[0], cg.color[1], cg.color[2]);
      material.color = toLumaGray(rawColor.r, rawColor.g, rawColor.b);
      material.roughness = 1.0;
      material.metalness = 0.0;
      material.envMapIntensity = 0;
      material.flatShading = materialSettings.flatShading;
      // Zero out emissive so no color bleeds through
      material.emissive = new THREE.Color(0, 0, 0);
      material.emissiveIntensity = 0;
      // Strip all color-carrying textures
      material.map = null;
      material.roughnessMap = null;
      material.metalnessMap = null;
      material.emissiveMap = null;
    } else {
      // Standard properties
      const rawColor = cg.isGhost
        ? new THREE.Color(0.6, 0.6, 0.8)
        : new THREE.Color(cg.color[0], cg.color[1], cg.color[2]);
      material.color = rawColor;
      material.metalness = cg.isGhost
        ? materialSettings.metalness
        : cg.material && cg.material.metalness >= 0
          ? cg.material.metalness
          : materialSettings.metalness;
      material.roughness = cg.isGhost
        ? 0.9
        : cg.material && cg.material.roughness >= 0
          ? cg.material.roughness
          : materialSettings.roughness;
      material.envMapIntensity = cg.isGhost
        ? 0.1
        : materialSettings.envMapIntensity;
      material.flatShading = materialSettings.flatShading;

      // Physical properties
      if (material instanceof THREE.MeshPhysicalMaterial && !cg.isGhost) {
        const v = (perObjVal: number | undefined, sceneVal: number) =>
          perObjVal !== undefined && perObjVal >= 0 ? perObjVal : sceneVal;

        material.clearcoat = v(
          cg.material?.clearcoat,
          materialSettings.clearcoat,
        );
        material.clearcoatRoughness = v(
          cg.material?.clearcoatRoughness,
          materialSettings.clearcoatRoughness,
        );
        material.sheen = v(cg.material?.sheen, materialSettings.sheen);
        material.sheenRoughness = v(
          cg.material?.sheenRoughness,
          materialSettings.sheenRoughness,
        );

        if (cg.material && cg.material.sheenColor[0] >= 0) {
          material.sheenColor = new THREE.Color(
            cg.material.sheenColor[0],
            cg.material.sheenColor[1],
            cg.material.sheenColor[2],
          );
        } else {
          material.sheenColor = new THREE.Color(...materialSettings.sheenColor);
        }

        material.iridescence = v(
          cg.material?.iridescence,
          materialSettings.iridescence,
        );
        material.iridescenceIOR = v(
          cg.material?.iridescenceIOR,
          materialSettings.iridescenceIOR,
        );

        const transmissionVal = v(
          cg.material?.transmission,
          materialSettings.transmission,
        );
        material.transmission = transmissionVal;
        material.ior = v(cg.material?.ior, materialSettings.ior);
        material.thickness = v(
          cg.material?.thickness,
          materialSettings.thickness,
        );

        if (cg.material && cg.material.attenuationColor[0] >= 0) {
          material.attenuationColor = new THREE.Color(
            cg.material.attenuationColor[0],
            cg.material.attenuationColor[1],
            cg.material.attenuationColor[2],
          );
        } else {
          material.attenuationColor = new THREE.Color(
            ...materialSettings.attenuationColor,
          );
        }
        material.attenuationDistance = v(
          cg.material?.attenuationDistance,
          materialSettings.attenuationDistance,
        );

        // Transmission needs transparency
        if (transmissionVal > 0) {
          material.transparent = true;
          material.side = THREE.DoubleSide;
          material.depthWrite = false;
        }
      }
    }

    material.opacity = finalOpacity;
    material.transparent = isTransparent;
    material.depthWrite = !isTransparent;
    material.side = isTransparent ? THREE.DoubleSide : THREE.FrontSide;
    material.needsUpdate = true;
  }, [
    material,
    materialSettings,
    cg,
    finalOpacity,
    isTransparent,
    grayscaleMode,
  ]);

  // Dispose on unmount or when material is recreated
  useEffect(() => {
    return () => {
      disposeMaterial(material);
    };
  }, [material]);

  // The active material: shader material takes priority over the PBR material.
  // While the shader worker hasn't sent READY yet, fall back to the PBR placeholder.
  const activeMaterial: THREE.Material = shaderMaterial ?? material;

  // Use AnimatedMotionMesh when this geometry has a motion() wrapper
  if (cg.motion) {
    return (
      <AnimatedMotionMesh
        geometry={cg.geometry}
        material={activeMaterial}
        motion={cg.motion}
        paused={animationsPaused}
        castShadow={!cg.isGhost && wireframeOpacity === 0}
        receiveShadow={!cg.isGhost && wireframeOpacity === 0}
        renderOrder={cg.isGhost ? 1000 : isTransparent ? 500 : 0}
        onClick={cg.interactive ? handleClick : undefined}
        onPointerOver={cg.interactive ? handlePointerOver : undefined}
        onPointerOut={cg.interactive ? handlePointerOut : undefined}
      />
    );
  }

  return (
    <mesh
      ref={meshRef}
      geometry={cg.geometry}
      material={activeMaterial}
      castShadow={!cg.isGhost && wireframeOpacity === 0}
      receiveShadow={!cg.isGhost && wireframeOpacity === 0}
      renderOrder={cg.isGhost ? 1000 : isTransparent ? 500 : 0}
      onClick={cg.interactive ? handleClick : undefined}
      onPointerOver={cg.interactive ? handlePointerOver : undefined}
      onPointerOut={cg.interactive ? handlePointerOut : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// Cloned geometry — a static copy of a source geometry at a given transform
// ---------------------------------------------------------------------------

/**
 * Render a single static clone of a source geometry.
 *
 * The clone shares the same BufferGeometry (no extra GPU upload) and creates
 * its own material from the source geometry's color/material properties.
 *
 * Coordinates follow OpenSCAD convention (Z-up). The parent group in
 * ColoredGeometriesMesh applies a -π/2 X rotation to convert to Three.js
 * Y-up space, so we express the clone transform in OpenSCAD space and it
 * inherits the same conversion automatically.
 */
function ClonedGeometryItem({
  source,
  clone,
  materialSettings,
  wireframeOpacity,
  grayscaleMode,
}: {
  source: ColoredGeometry;
  clone: CloneDef;
  materialSettings: MaterialSettings;
  wireframeOpacity: number;
  grayscaleMode: boolean;
}) {
  const baseTransparent = source.color[3] < 1 || source.isGhost;
  const baseOpacity = source.isGhost ? 0.3 : source.color[3];
  const finalOpacity = baseOpacity * (1 - wireframeOpacity);
  const isTransparent = baseTransparent || wireframeOpacity > 0;

  const { needsPhysicalMaterial: needsPhysical } = useMemo(
    () => ({ needsPhysicalMaterial: needsPhysicalMaterial(source.material, materialSettings) }),
    [source.material, materialSettings],
  );

  const material = useMemo(() => {
    const rawColor = source.isGhost
      ? new THREE.Color(0.6, 0.6, 0.8)
      : new THREE.Color(source.color[0], source.color[1], source.color[2]);

    const meshColor = grayscaleMode
      ? (() => {
          const luma = 0.2126 * rawColor.r + 0.7152 * rawColor.g + 0.0722 * rawColor.b;
          return new THREE.Color(luma, luma, luma);
        })()
      : rawColor;

    const effectiveSettings: MaterialSettings = grayscaleMode
      ? { ...materialSettings, roughness: 1.0, metalness: 0.0, envMapIntensity: 0 }
      : materialSettings;

    return createMaterial(meshColor, effectiveSettings, {
      perObject: grayscaleMode ? undefined : source.material,
      isGhost: source.isGhost,
      opacity: finalOpacity,
      transparent: isTransparent,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, needsPhysical, grayscaleMode]);

  useEffect(() => {
    return () => {
      disposeMaterial(material);
    };
  }, [material]);

  // Convert Euler XYZ rotation from degrees to radians
  const rotX = (clone.rotation[0] * Math.PI) / 180;
  const rotY = (clone.rotation[1] * Math.PI) / 180;
  const rotZ = (clone.rotation[2] * Math.PI) / 180;

  return (
    <mesh
      geometry={source.geometry}
      material={material}
      position={clone.position}
      rotation={[rotX, rotY, rotZ]}
      scale={clone.scale}
      castShadow={!source.isGhost && wireframeOpacity === 0}
      receiveShadow={!source.isGhost && wireframeOpacity === 0}
      renderOrder={source.isGhost ? 1000 : isTransparent ? 500 : 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Coloured geometries
// ---------------------------------------------------------------------------

function ColoredGeometriesMesh({
  coloredGeometries,
  clones,
  materialSettings,
  wireframeOpacity,
  grayscaleMode,
  textureMapRef,
  textureVersion,
  shaderMaterialMapRef,
  shaderVersion,
  onInteraction,
  animationsPaused = false,
}: {
  coloredGeometries: ColoredGeometry[];
  clones?: CloneDef[];
  materialSettings: MaterialSettings;
  wireframeOpacity: number;
  grayscaleMode: boolean;
  textureMapRef?: React.MutableRefObject<
    Map<string, Map<string, THREE.Texture>>
  >;
  textureVersion?: number;
  shaderMaterialMapRef?: React.MutableRefObject<
    Map<string, THREE.ShaderMaterial>
  >;
  shaderVersion?: number;
  onInteraction?: (config: InteractiveConfig) => void;
  animationsPaused?: boolean;
}) {
  const sorted = useMemo(
    () =>
      [...coloredGeometries].sort((a, b) => {
        const aOrder = a.isGhost ? 2 : a.color[3] < 1 ? 1 : 0;
        const bOrder = b.isGhost ? 2 : b.color[3] < 1 ? 1 : 0;
        return aOrder - bOrder;
      }),
    [coloredGeometries],
  );

  // Build a map from cloneKey → ColoredGeometry for O(1) lookup
  const cloneSourceMap = useMemo(() => {
    const map = new Map<string, ColoredGeometry>();
    for (const cg of coloredGeometries) {
      if (cg.cloneKey) {
        map.set(cg.cloneKey, cg);
      }
    }
    return map;
  }, [coloredGeometries]);

  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      {sorted.map((cg) => (
        <ColoredGeometryItem
          key={cg.geometry.uuid}
          cg={cg}
          materialSettings={materialSettings}
          wireframeOpacity={wireframeOpacity}
          grayscaleMode={grayscaleMode}
          textureMapRef={textureMapRef}
          textureVersion={textureVersion}
          shaderMaterialMapRef={shaderMaterialMapRef}
          shaderVersion={shaderVersion}
          onInteraction={onInteraction}
          animationsPaused={animationsPaused}
        />
      ))}
      {clones && clones.map((cloneDef, i) => {
        const source = cloneSourceMap.get(cloneDef.key);
        if (!source) return null;
        return (
          <ClonedGeometryItem
            key={`clone-${cloneDef.key}-${i}`}
            source={source}
            clone={cloneDef}
            materialSettings={materialSettings}
            wireframeOpacity={wireframeOpacity}
            grayscaleMode={grayscaleMode}
          />
        );
      })}
    </group>
  );
}

// ---------------------------------------------------------------------------
// Single geometry (monochrome)
// ---------------------------------------------------------------------------

function SingleGeometryMesh({
  geometry,
  color,
  materialSettings,
  wireframeOpacity,
  grayscaleMode,
}: {
  geometry: THREE.BufferGeometry;
  color: string;
  materialSettings: MaterialSettings;
  wireframeOpacity: number;
  grayscaleMode: boolean;
}) {
  const usePhysical =
    !grayscaleMode && needsPhysicalMaterial(undefined, materialSettings);
  const isTransparent = wireframeOpacity > 0;

  const material = useMemo(() => {
    const effectiveColor = grayscaleMode
      ? (() => {
          const c = new THREE.Color(color);
          return toLumaGray(c.r, c.g, c.b).getStyle();
        })()
      : color;

    const effectiveSettings: MaterialSettings = grayscaleMode
      ? {
          ...materialSettings,
          roughness: 1.0,
          metalness: 0.0,
          envMapIntensity: 0,
        }
      : materialSettings;

    return createMaterial(effectiveColor, effectiveSettings, {
      opacity: 1 - wireframeOpacity,
      transparent: isTransparent,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, usePhysical, grayscaleMode]);

  // Update in-place
  useEffect(() => {
    if (grayscaleMode) {
      const c = new THREE.Color(color);
      material.color = toLumaGray(c.r, c.g, c.b);
      material.roughness = 1.0;
      material.metalness = 0.0;
      material.envMapIntensity = 0;
      material.flatShading = materialSettings.flatShading;
      material.emissive = new THREE.Color(0, 0, 0);
      material.emissiveIntensity = 0;
    } else {
      material.color = new THREE.Color(color);
      material.metalness = materialSettings.metalness;
      material.roughness = materialSettings.roughness;
      material.envMapIntensity = materialSettings.envMapIntensity;
      material.flatShading = materialSettings.flatShading;

      if (material instanceof THREE.MeshPhysicalMaterial) {
        material.clearcoat = materialSettings.clearcoat;
        material.clearcoatRoughness = materialSettings.clearcoatRoughness;
        material.sheen = materialSettings.sheen;
        material.sheenRoughness = materialSettings.sheenRoughness;
        material.sheenColor = new THREE.Color(...materialSettings.sheenColor);
        material.iridescence = materialSettings.iridescence;
        material.iridescenceIOR = materialSettings.iridescenceIOR;
        material.transmission = materialSettings.transmission;
        material.ior = materialSettings.ior;
        material.thickness = materialSettings.thickness;
        material.attenuationColor = new THREE.Color(
          ...materialSettings.attenuationColor,
        );
        material.attenuationDistance = materialSettings.attenuationDistance;

        if (materialSettings.transmission > 0) {
          material.transparent = true;
          material.side = THREE.DoubleSide;
          material.depthWrite = false;
        }
      }
    }

    material.opacity = 1 - wireframeOpacity;
    material.transparent = isTransparent;
    material.needsUpdate = true;
  }, [
    material,
    color,
    materialSettings,
    wireframeOpacity,
    isTransparent,
    grayscaleMode,
  ]);

  useEffect(() => {
    return () => {
      disposeMaterial(material);
    };
  }, [material]);

  return (
    <mesh
      geometry={geometry}
      material={material}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0, 0]}
      castShadow={wireframeOpacity === 0}
      receiveShadow={wireframeOpacity === 0}
    />
  );
}
